/** Géométrie dessinée du fuseau (CorridorLayers), en fonctions pures : depuis la réponse du serveur, ou depuis le
 *  modèle éditable pendant un geste (règle B28 : l'aperçu reste affiché jusqu'à la réponse), et mélange de deux
 *  géométries quand le fuseau est remplacé (retour à l'automatique, annulation ; docs/14 §2.10) : interpolation
 *  sommet par sommet si les nombres de points sont égaux, sinon fondu enchaîné entre deux sources. */

import type { Corridor, CorridorModel } from "../../api/types";
import {
  bearingDeg,
  endBearing,
  extendEnds,
  gapToPolylines,
  helperDistanceM,
  labelLast,
  moveM,
  overshootExtensionM,
  pointAlongPolyline,
  type LonLat,
} from "../geo";
import { frameOf, helperDistance } from "./corridorFrame";
import { linePoints, presentLines, previewHours, type LineName } from "./corridorEdit";
import { metersPerPixel } from "./handles";

/** Une LIMA dessinée : tracé fini à l'extrémité qui porte l'étiquette. */
export interface ShapeLine {
  name: LineName;
  coords: LonLat[];
}

export interface ShapeMark {
  at: LonLat;
  rotation: number;
}

/** Fuseau tel qu'il est dessiné : limite de référence (`upper`, droite géographique) et l'autre (`lower`),
 *  LIMA, symboles d'échelon. */
export interface CorridorShape {
  upper: LonLat[];
  lower: LonLat[];
  lines: ShapeLine[];
  marks: ShapeMark[];
}

/** Symbole d'échelon : 50 m vers l'intérieur du fuseau (mapdraw.ECHELON_OFFSET_M). */
const ECHELON_OFFSET_M = 50;

function polylineLength(points: readonly LonLat[]): number {
  let total = 0;
  for (let i = 0; i + 1 < points.length; i++) total += helperDistance(points[i]!, points[i + 1]!);
  return total;
}

/** Milieu de la longueur d'une polyligne et cap du segment qui le porte (mapdraw.midpoint_and_bearing). */
export function midpointAndBearing(polyline: LonLat[]): [LonLat, number] | null {
  if (polyline.length < 2) return null;
  const target = polylineLength(polyline) / 2;
  let cumulative = 0;
  for (let i = 0; i + 1 < polyline.length; i++) {
    const a = polyline[i]!;
    const b = polyline[i + 1]!;
    const length = helperDistance(a, b);
    if (cumulative + length >= target) return [pointAlongPolyline(polyline, target) ?? a, bearingDeg(a, b)];
    cumulative += length;
  }
  const n = polyline.length;
  return [pointAlongPolyline(polyline, target) ?? polyline[n - 1]!, bearingDeg(polyline[n - 2]!, polyline[n - 1]!)];
}

/** Symboles d'échelon d'un fuseau (mapdraw.echelon_marks) : un par limite, au milieu, 50 m vers l'intérieur,
 *  tourné de (cap local + 90°). `upper` : limite de référence. */
export function echelonMarks(upper: LonLat[], lower: LonLat[]): ShapeMark[] {
  const marks: ShapeMark[] = [];
  for (const [rail, reference] of [
    [upper, true],
    [lower, false],
  ] as const) {
    const found = midpointAndBearing(rail);
    if (!found) continue;
    const [mid, bearing] = found;
    const inward = reference ? bearing - 90 : bearing + 90;
    marks.push({ at: moveM(mid, inward, ECHELON_OFFSET_M), rotation: bearing + 90 });
  }
  return marks;
}

/** Géométrie de la réponse du serveur. */
export function shapeFromServer(corridor: Corridor): CorridorShape {
  return {
    upper: corridor.upper,
    lower: corridor.lower,
    lines: corridor.lines.map((line) => ({ name: line.name, coords: line.coordinates })),
    marks: corridor.marks.map((mark) => ({ at: mark.coordinates, rotation: mark.rotation })),
  };
}

/** Géométrie du modèle éditable, comme le serveur la dessine pour un fuseau personnalisé (`user_corridor.display`) :
 *  limite de référence = droite géographique, LIMA par `linePoints` (prolongées de 500 m), étiquette à
 *  l'extrémité la plus proche de la limite gauche. */
export function shapeFromModel(model: CorridorModel): CorridorShape {
  const upper = model.right.map((p) => [p[0], p[1]] as LonLat);
  const lower = model.left.map((p) => [p[0], p[1]] as LonLat);
  if (upper.length < 2 || lower.length !== upper.length) return { upper, lower, lines: [], marks: [] };
  const frame = frameOf(model);
  const lines = presentLines(model).map((name) => ({
    name,
    coords: labelLast(linePoints(model, name, frame), lower),
  }));
  return { upper, lower, lines, marks: echelonMarks(upper, lower) };
}

/** Heures (après le début de l'exercice) affichées par les étiquettes pendant un geste : GDH effectifs du
 *  modèle (règle K3). */
export function modelHours(model: CorridorModel): Partial<Record<LineName, number>> {
  const hours = previewHours(model);
  const out: Partial<Record<LineName, number>> = {};
  for (const name of presentLines(model)) out[name] = hours[name];
  return out;
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpPoint = (a: LonLat, b: LonLat, t: number): LonLat => [lerp(a[0], b[0], t), lerp(a[1], b[1], t)];

function lerpAngle(from: number, to: number, t: number): number {
  let diff = (to - from) % 360;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  return from + diff * t;
}

/** Deux géométries interpolables sommet par sommet : mêmes nombres de points sur chaque limite, mêmes LIMA avec
 *  les mêmes nombres de points, même nombre de symboles. */
export function interpolable(a: CorridorShape, b: CorridorShape): boolean {
  if (a.upper.length !== b.upper.length || a.lower.length !== b.lower.length) return false;
  if (a.marks.length !== b.marks.length || a.lines.length !== b.lines.length) return false;
  return b.lines.every((line) => a.lines.find((l) => l.name === line.name)?.coords.length === line.coords.length);
}

/** Géométrie interpolée à l'avancement `t` (0 : `a`, 1 : `b`) ; suppose `interpolable(a, b)`. */
export function lerpShape(a: CorridorShape, b: CorridorShape, t: number): CorridorShape {
  if (t >= 1) return b;
  return {
    upper: b.upper.map((p, i) => lerpPoint(a.upper[i]!, p, t)),
    lower: b.lower.map((p, i) => lerpPoint(a.lower[i]!, p, t)),
    lines: b.lines.map((line) => {
      const from = a.lines.find((l) => l.name === line.name)!;
      return { name: line.name, coords: line.coords.map((p, i) => lerpPoint(from.coords[i]!, p, t)) };
    }),
    marks: b.marks.map((mark, i) => ({
      at: lerpPoint(a.marks[i]!.at, mark.at, t),
      rotation: lerpAngle(a.marks[i]!.rotation, mark.rotation, t),
    })),
  };
}

/** Image d'un remplacement à l'avancement adouci `t`, élément par élément : les limites (et leurs symboles
 *  d'échelon) glissent sommet par sommet si leurs nombres de points sont égaux, sinon se fondent ; chaque LIMA de
 *  même nombre de points glisse, les autres se fondent une à une (la barre d'aperçu d'une LIMA glissée qui laisse
 *  place à la ligne adaptée au terrain ne fait pas clignoter le reste du fuseau). `ghost` : ce qui s'efface
 *  (limites et symboles non interpolables, LIMA remplacées ou retirées), à l'opacité `1 − t`. */
export interface ShapeFrame {
  shape: CorridorShape;
  /** Opacité des limites et des symboles d'échelon dessinés (1 s'ils glissent). */
  railsOpacity: number;
  /** Opacité des LIMA qui apparaissent en fondu (absente : 1, la LIMA glisse ou ne change pas). */
  lineOpacity: Partial<Record<LineName, number>>;
  ghost: { shape: CorridorShape; opacity: number } | null;
}

/** Limites et symboles interpolables : mêmes nombres de points et de symboles. */
export function railsInterpolable(a: CorridorShape, b: CorridorShape): boolean {
  return a.upper.length === b.upper.length && a.lower.length === b.lower.length && a.marks.length === b.marks.length;
}

export function blendShapes(a: CorridorShape, b: CorridorShape, t: number): ShapeFrame {
  const x = Math.min(1, Math.max(0, t));
  if (x >= 1) return { shape: b, railsOpacity: 1, lineOpacity: {}, ghost: null };
  const rails = railsInterpolable(a, b);
  const lineOpacity: ShapeFrame["lineOpacity"] = {};
  const ghostLines: ShapeLine[] = [];
  const lines = b.lines.map((line) => {
    const from = a.lines.find((l) => l.name === line.name);
    if (from && from.coords.length === line.coords.length)
      return { name: line.name, coords: line.coords.map((p, i) => lerpPoint(from.coords[i]!, p, x)) };
    lineOpacity[line.name] = x;
    if (from) ghostLines.push(from);
    return line;
  });
  for (const line of a.lines) if (!b.lines.some((l) => l.name === line.name)) ghostLines.push(line);
  const shape: CorridorShape = rails
    ? {
        upper: b.upper.map((p, i) => lerpPoint(a.upper[i]!, p, x)),
        lower: b.lower.map((p, i) => lerpPoint(a.lower[i]!, p, x)),
        lines,
        marks: b.marks.map((mark, i) => ({
          at: lerpPoint(a.marks[i]!.at, mark.at, x),
          rotation: lerpAngle(a.marks[i]!.rotation, mark.rotation, x),
        })),
      }
    : { upper: b.upper, lower: b.lower, lines, marks: b.marks };
  const ghost =
    !rails || ghostLines.length
      ? {
          shape: {
            upper: rails ? [] : a.upper,
            lower: rails ? [] : a.lower,
            lines: ghostLines,
            marks: rails ? [] : a.marks,
          },
          opacity: 1 - x,
        }
      : null;
  return { shape, railsOpacity: rails ? 1 : x, lineOpacity, ghost };
}

/** Même géométrie (aucune transition à jouer). */
export function sameShape(a: CorridorShape | null, b: CorridorShape | null): boolean {
  if (a === b) return true;
  if (!a || !b || !interpolable(a, b)) return false;
  const same = (p: LonLat, q: LonLat) => Math.abs(p[0] - q[0]) < 1e-9 && Math.abs(p[1] - q[1]) < 1e-9;
  return (
    a.upper.every((p, i) => same(p, b.upper[i]!)) &&
    a.lower.every((p, i) => same(p, b.lower[i]!)) &&
    b.lines.every((line) => {
      const other = a.lines.find((l) => l.name === line.name)!;
      return line.coords.every((p, i) => same(other.coords[i]!, p));
    })
  );
}

/* --- Dépassement des LIMA et place de leur étiquette (affichage seulement) ------------------------------------ */

/** Dépassement minimal d'une LIMA au-delà de chaque limite, à l'écran : les données le portent déjà (500 m), mais
 *  aux zooms du camp ce n'est que quelques pixels. */
export const LIMA_OVERSHOOT_PX = 12;
/** Hors édition, le dépassement ajouté à l'écran ne passe pas 5 % de la largeur locale du fuseau (demandes d'Alexis
 *  du 04/10/2026 : un terrain libre s'affiche comme un camp, puis « applique aussi le plafond aux camps ») : un fuseau
 *  vu de loin, étroit à l'écran, garde l'aspect d'un camp à son zoom d'entrée au lieu de LIMA qui débordent largement
 *  de part et d'autre ; au zoom d'entrée d'un camp, rien ne change (12 px). Les 500 m des données ne sont jamais
 *  rognés. */
export const LIMA_OVERSHOOT_WIDTH_RATIO = 0.05;
/** Plafond relatif du dépassement à l'écran (`overshootLines`) : tout fuseau, camp ou terrain libre, hors édition ;
 *  le mode édition (poignées et halo) n'en reçoit aucun. */
export function limaOvershootRatio(editing: boolean): number | undefined {
  return editing ? undefined : LIMA_OVERSHOOT_WIDTH_RATIO;
}
/** Plancher du dépassement plafonné : la LIMA dépasse toujours visiblement (règle du 02/10), au moins de 4 px. */
export const LIMA_OVERSHOOT_FLOOR_PX = 4;
/** En édition, la ligne continue nettement au-delà de la poignée d'extrémité posée sur la limite : halo blanc de
 *  la poignée saisie (r 13, flou), puis au moins 7 px de trait net. */
export const LIMA_OVERSHOOT_EDIT_PX = 20;
/** Écart entre la fin de la LIMA et son étiquette. */
export const LABEL_GAP_PX = 4;

/** Zoom de calcul du dépassement : arrondi au quart inférieur, pour ne recalculer qu'à chaque quart de niveau
 *  (le dépassement réel reste entre `minPx` et 1,19 × `minPx`). */
export function overshootZoom(zoom: number): number {
  return Math.floor(zoom * 4) / 4;
}

/** LIMA (ou lignes de la ZO) dessinées : chaque extrémité prolongée dans l'axe de son segment terminal pour que son
 *  dépassement au-delà de la limite la plus proche (`boundaries`) vaille max(dépassement des données, `minPx` au
 *  zoom donné). Avec `widthRatio` (hors édition), le dépassement visé est ramené à `widthRatio` × la largeur locale
 *  du fuseau (distance des deux extrémités moins leurs dépassements), sans descendre sous `LIMA_OVERSHOOT_FLOOR_PX`.
 *  Le tracé des données n'est jamais raccourci ; même nombre de points ; les autres champs sont gardés. */
export function overshootLines<T extends { coords: LonLat[] }>(
  lines: readonly T[],
  boundaries: readonly LonLat[][],
  minPx: number,
  zoom: number,
  widthRatio?: number,
): T[] {
  return lines.map((line) => {
    const first = line.coords[0];
    const last = line.coords[line.coords.length - 1];
    if (!first || !last || line.coords.length < 2) return line;
    const gapFirst = gapToPolylines(first, boundaries);
    const gapLast = gapToPolylines(last, boundaries);
    const width = widthRatio ? helperDistanceM(first, last) - gapFirst - gapLast : Infinity;
    const extension = (tip: LonLat, gap: number) => {
      const mpp = metersPerPixel(zoom, tip[1]);
      const capped = widthRatio && width > 0 && mpp > 0;
      const px = capped ? Math.min(minPx, Math.max(LIMA_OVERSHOOT_FLOOR_PX, (widthRatio * width) / mpp)) : minPx;
      return overshootExtensionM(gap, px, mpp);
    };
    const start = extension(first, gapFirst);
    const end = extension(last, gapLast);
    return start > 0 || end > 0 ? { ...line, coords: extendEnds(line.coords, start, end) } : line;
  });
}

/** Place de l'étiquette au-delà de l'extrémité d'une LIMA dont le segment terminal a le cap `bearing` (°, vers
 *  l'extrémité) sur une carte orientée au cap `mapBearing`, pour un repère ancré au centre de la puce :
 *  `gap` (px) écarte la puce de `gapPx` dans l'axe de la ligne, `shift` la pousse d'une fraction de sa demi-taille
 *  (direction projetée sur le carré : l'un des deux vaut ±1). Le bord de la puce passe ainsi par le point situé à
 *  `gapPx` au-delà de l'extrémité et la puce s'étend du côté opposé à la ligne. Variation continue avec le cap
 *  (aucun saut, aucun repère recréé) ; ne dépend que des caps : stable pendant un déplacement. */
export function labelPlacement(
  bearing: number,
  mapBearing = 0,
  gapPx = LABEL_GAP_PX,
): { gap: [number, number]; shift: [number, number] } {
  const screen = (((bearing - mapBearing) % 360) + 360) % 360;
  const rad = (screen * Math.PI) / 180;
  const ux = Math.sin(rad);
  const uy = -Math.cos(rad); // y vers le bas à l'écran
  const m = Math.max(Math.abs(ux), Math.abs(uy));
  const round = (v: number) => Math.round(v * 1000) / 1000 + 0; // + 0 : pas de −0
  return { gap: [round(ux * gapPx), round(uy * gapPx)], shift: [round(ux / m), round(uy / m)] };
}

/** Transformation CSS de la puce (repère ancré au centre) : les pourcentages portent sur sa propre taille, ce
 *  qui évite de la mesurer (largeur variable avec le GDH et la langue). */
export function labelTransform(place: { gap: [number, number]; shift: [number, number] }): string {
  const [gx, gy] = place.gap;
  const [sx, sy] = place.shift;
  return `translate(calc(${sx * 50}% + ${gx}px), calc(${sy * 50}% + ${gy}px))`;
}

/** Étiquettes d'un tracé : extrémité côté étiquette (la dernière) et cap du segment qui y mène. */
export function lineLabels(lines: readonly ShapeLine[]): { name: LineName; at: LonLat; bearing: number }[] {
  return lines.flatMap((line) => {
    const at = line.coords[line.coords.length - 1];
    if (!at) return [];
    return [{ name: line.name, at, bearing: endBearing(line.coords, true) ?? 90 }];
  });
}
